import { useState } from 'react'
import {
  HeaderChildrenContainer,
  Wrapper,
} from './components/display/commonStyledComponents'
import { Button, Check, EmptyState, NoDataIcon, PageHeader } from '@lmc/ui-kit'
import { Form as AntdForm } from 'antd'
import { generalConstants } from 'locale/general'
import { showToast } from 'common/utils'
import { NAMESPACE } from 'locale'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useParams } from 'react-router-dom'
import useIncidentReportByLog from 'hooks/useIncidentReport/useIncidentReportByLog'
import { Spinner } from 'components/generic'
import useIncidentReport from 'hooks/useIncidentReport/useIncidentReport'
import dayjs from 'dayjs'
import IncidentCaseForm, {
  IncidentReportCreateCaseForm,
} from './components/forms/IncidentCaseForm'
import { IncidentReportCreateCaseBody } from '@lmc/ts-common'
import { getCombinedTimeIso } from './utils'
import { getNewFilesFormData } from './components/UploadAttachmentsOnSaveInput'
import NavigationModal from 'components/NavigationModal/NavigationModal'

const IncidentReportCreateCaseScreen = () => {
  const [form] = AntdForm.useForm()
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const [isDirty, setIsDirty] = useState(false)
  const [isUpdateLoading, setIsUpdateLoading] = useState(false)
  const navigate = useNavigate()
  const { id: incidentLogId } = useParams()
  const { incidentReport, isIncidentReportLoading, refetch } =
    useIncidentReportByLog({
      incidentLogId,
    })
  const { createIncidentReportCase, uploadIncidentReportFiles } =
    useIncidentReport({
      incidentReportId: incidentReport?.id,
      incidentLogId,
      onUpdateSuccess: () => {
        showToast({
          title: t(incidentReportsConstants.CASE_UPDATED),
        })
      },
      onUploadFilesSuccess: refetch,
    })

  if (isIncidentReportLoading) return <Spinner />
  if (!incidentReport)
    return (
      <EmptyState
        icon={<NoDataIcon />}
        title={t(incidentReportsConstants.LOG_NOT_FOUND)}
      />
    )

  const initialValues: Partial<IncidentReportCreateCaseForm<string>> = {
    ...incidentReport,
    severity: null,
    otherPeopleInvolved: null,
    physicalIntervention: { hasBeenApplied: false },
    restraints: { hasBeenApplied: false },
    chemicalRestraints: { hasBeenApplied: false },
    residentInjuries: { hasBeenApplied: false },
    carersInjuries: { hasBeenApplied: false },
    otherPartiesInjuries: { hasBeenApplied: false },
    damages: { hasBeenApplied: false },
    prnMedication: { hasBeenApplied: false },
    otherClientsInvolved: [],
    serviceUsersInvolved: [],
    attachments: [],
    followUpActions: {
      actions: [],
      notificationRequired: false,
      safeguardingActionsRequired: false,
    },
    log: {
      ...incidentReport.log,
      goals: incidentReport.log.goals?.map((goal) => goal.id) || [],
      dateLogged: dayjs(incidentReport.log.timeLogged),
      timeLogged: dayjs(incidentReport.log.timeLogged),
      item: incidentReport.log.item?.id,
    },
  }

  const onFinish = async (values: IncidentReportCreateCaseForm<string>) => {
    try {
      setIsUpdateLoading(true)
      setIsDirty(false)

      const incidentReportCreateCaseBody: IncidentReportCreateCaseBody = {
        ...values,
        log: {
          ...values.log,
          timeLogged: getCombinedTimeIso(
            values?.log?.dateLogged,
            values?.log?.timeLogged
          ),
        },
        followUpActions: {
          ...values.followUpActions,
          // deletes id from result
          actions: values?.followUpActions?.actions?.map((action) => ({
            title: action.title,
            status: action.status,
			assignedUserId: action.assignedUserId ?? null
          })),
        },
      }

      await createIncidentReportCase(incidentReportCreateCaseBody)

      const formData = getNewFilesFormData(values.attachments)
      if (formData) {
        await uploadIncidentReportFiles(formData)
      }

      navigate(`../`, { relative: 'path' })
    } catch {
      showToast({
        title: t(generalConstants.ERROR, { ns: NAMESPACE.GENERAL }),
        type: 'error',
      })
    }

    setIsUpdateLoading(false)
  }

  const onValuesChange = (values: any) => {
    if (values) {
      setIsDirty(true)
    }
  }

  return (
    <>
      <PageHeader
        sticky
        title={t(incidentReportsConstants.CREATING_INCIDENT_CASE)}
        onBack={() => navigate('../', { relative: 'path' })}
      >
        <HeaderChildrenContainer>
          <Button onClick={() => navigate('../', { relative: 'path' })}>
            {t(incidentReportsConstants.CANCEL)}
          </Button>
          <Button
            variant="primary"
            type="submit"
            onClick={form.submit}
            isLoading={isUpdateLoading}
          >
            {t(incidentReportsConstants.SAVE)} <Check color="white" />
          </Button>
        </HeaderChildrenContainer>
      </PageHeader>
      <Wrapper>
        <IncidentCaseForm
          form={form}
          onFinish={onFinish}
          onValuesChange={onValuesChange}
          initialValues={initialValues}
          incidentReport={incidentReport}
        />
        <NavigationModal canNavigate={!isDirty} />
      </Wrapper>
    </>
  )
}

export default IncidentReportCreateCaseScreen
